import { z } from 'zod';
import { CONDITIONS, ITEM_TYPES, SUB_CONDITIONS } from './item-types';
import { microToDecimal, toMicro } from './money';
import { ORDER_ACTIONS, ORDER_STATUSES } from './orders';
import { PRICE_GUIDE_REGIONS } from './sell-through';

// The web app imports only this entry point, so browser-safe helpers are re-exported here.
export * from './item-types';
export * from './images';
export * from './money';
export * from './orders';
export * from './sell-through';
export * from './pricing';

// ---------------------------------------------------------------- building blocks

export const itemTypeSchema = z.enum(ITEM_TYPES);
export const conditionSchema = z.enum(CONDITIONS);
export const subConditionSchema = z.enum(SUB_CONDITIONS);

/** Accepts 0.25, "0.25", "0,25" or "£0.25"; yields a four-place decimal string. */
export const moneySchema = z
  .union([z.number(), z.string()])
  .transform((v, ctx) => {
    const micro = toMicro(v);
    if (micro === null || micro < 0) {
      ctx.addIssue({ code: 'custom', message: 'Enter a money amount of zero or more' });
      return z.NEVER;
    }
    return microToDecimal(micro);
  });

const blank = (v: unknown) => (v === '' ? undefined : v);
const optionalNumber = z.preprocess(blank, z.coerce.number().optional());
const optionalInt = z.preprocess(blank, z.coerce.number().int().optional());
const optionalPercent = z.preprocess(blank, z.coerce.number().min(0).max(100).optional());
const optionalNonNegative = z.preprocess(blank, z.coerce.number().min(0).max(100000).optional());
const optionalBool = z.preprocess(
  (v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : blank(v)),
  z.boolean().optional(),
);
const optionalText = z.preprocess(blank, z.string().trim().max(200).optional());

export const LOT_STATUSES = ['draft', 'live', 'stockroom', 'sold_out'] as const;
export const lotStatusSchema = z.enum(LOT_STATUSES);

// ---------------------------------------------------------------- inventory search

export const LOT_SORTS = ['itemNo', 'name', 'qty', 'incoming', 'price', 'updated', 'sellThrough', 'monthsOfStock'] as const;

/** Ready-made views of the lots that need a look. */
export const ATTENTION_VIEWS = ['no_price', 'losing', 'thin', 'no_bin', 'below_reorder', 'held', 'can_build', 'free'] as const;
export type AttentionView = (typeof ATTENTION_VIEWS)[number];
export const ATTENTION_LABEL: Record<AttentionView, string> = {
  no_price: 'Needs a price',
  losing: 'Priced below cost',
  thin: 'Thin margin',
  no_bin: 'No bin',
  below_reorder: 'Below reorder level',
  held: 'Damaged or held',
  can_build: 'Can build',
  free: 'Free stock',
};

export const lotSearchSchema = z.object({
  /** One of the ready-made views (needs a price, below cost, no bin, and so on). */
  attention: z.preprocess(blank, z.enum(ATTENTION_VIEWS).optional()),
  q: optionalText,
  itemType: z.preprocess(blank, itemTypeSchema.optional()),
  itemNo: optionalText,
  condition: z.preprocess(blank, conditionSchema.optional()),
  colorId: optionalInt,
  categoryId: optionalInt,
  ean: optionalText,
  /** Location code. A trailing * matches any suffix, so "A*" finds A1, A2, AB… */
  location: optionalText,
  status: z.preprocess(blank, lotStatusSchema.optional()),
  priceLocked: optionalBool,
  qtyMin: optionalInt,
  qtyMax: optionalInt,
  priceMin: optionalNumber,
  priceMax: optionalNumber,
  /**
   * Market sell-through filters, from the cached BrickLink price guide (all sellers). Percentages, 0 to 100.
   * A lot with no figures yet matches none of them.
   */
  sellThroughMin: optionalPercent,
  sellThroughMax: optionalPercent,
  /** Months the market's listings last at the last 6 months' pace. "No recent sales" counts as endless. */
  monthsMin: optionalNonNegative,
  monthsMax: optionalNonNegative,
  /** have: the lot has figures. none: it has none yet (never fetched, or BrickLink had nothing). */
  marketData: z.preprocess(blank, z.enum(['have', 'none']).optional()),
  /** By default lots with no stock are hidden. */
  includeEmpty: optionalBool,
  sort: z.preprocess(blank, z.enum(LOT_SORTS).default('itemNo')),
  dir: z.preprocess(blank, z.enum(['asc', 'desc']).default('asc')),
  page: z.preprocess(blank, z.coerce.number().int().min(1).default(1)),
  pageSize: z.preprocess(blank, z.coerce.number().int().min(1).max(200).default(50)),
});
export type LotSearch = z.infer<typeof lotSearchSchema>;

/** The filter part only, as stored in a saved search. */
export const lotFilterSchema = lotSearchSchema.omit({ sort: true, dir: true, page: true, pageSize: true });
export type LotFilter = z.infer<typeof lotFilterSchema>;

export const savedSearchInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  query: lotFilterSchema,
});

// ---------------------------------------------------------------- lots

export const lotPatchSchema = z
  .object({
    subCondition: subConditionSchema.nullable(),
    price: moneySchema.nullable(),
    priceLocked: z.boolean(),
    /** Keep the price up to date with the market by this rule. Null stops it. Changing the price by hand also stops it. */
    priceRule: z.enum(['breakeven', 'floor', 'cost_plus', 'quick', 'market', 'patient']).nullable(),
    remarks: z.string().trim().max(500).nullable(),
    description: z.string().trim().max(500).nullable(),
    bulk: z.number().int().min(1).max(100000),
    ean: z.string().trim().max(20).nullable(),
    status: lotStatusSchema,
  })
  .partial();

export const bulkEditSchema = z.object({
  target: z.union([
    z.object({ ids: z.array(z.number().int()).min(1).max(50000) }),
    z.object({ filter: lotFilterSchema }),
  ]),
  action: z.discriminatedUnion('type', [
    z.object({ type: z.literal('set_price'), price: moneySchema }),
    z.object({ type: z.literal('adjust_price'), percent: z.number().min(-90).max(1000) }),
    z.object({ type: z.literal('lock_price') }),
    z.object({ type: z.literal('unlock_price') }),
    z.object({ type: z.literal('set_price_rule'), rule: z.enum(['breakeven', 'floor', 'cost_plus', 'quick', 'market', 'patient']).nullable() }),
    z.object({ type: z.literal('set_location'), locationId: z.number().int() }),
    z.object({ type: z.literal('set_status'), status: lotStatusSchema }),
  ]),
  /** Report what would change without changing it. */
  dryRun: z.boolean().default(false),
});
export type BulkEdit = z.infer<typeof bulkEditSchema>;

// ---------------------------------------------------------------- locations

const locationFields = {
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .refine((c) => !c.includes('*'), 'A location code cannot contain *'),
  name: z.string().trim().max(120).nullish(),
  parentId: z.number().int().nullish(),
  warehouse: z.string().trim().min(1).max(60),
  /** Order in which a picker walks the locations. */
  walkOrder: z.number().int().min(0).max(1_000_000),
  notes: z.string().trim().max(500).nullish(),
};

export const locationInputSchema = z.object({
  ...locationFields,
  warehouse: locationFields.warehouse.default('main'),
  walkOrder: locationFields.walkOrder.default(0),
});

/** Edits never carry defaults: leaving a field out must leave it alone. */
export const locationPatchSchema = z.object(locationFields).partial();

// ---------------------------------------------------------------- purchasing

export const VAT_SCHEMES = ['none', 'standard', 'margin'] as const;
export const ALLOCATION_METHODS = ['by_value', 'equal'] as const;

const purchaseFields = {
  purchasedOn: z.iso.date(),
  description: z.string().trim().max(200),
  defaultCondition: conditionSchema,
  totalCost: moneySchema,
  currency: z.string().length(3),
  sellerName: z.string().trim().max(120).nullish(),
  sellerContact: z.string().trim().max(500).nullish(),
  vatScheme: z.enum(VAT_SCHEMES),
  allocationMethod: z.enum(ALLOCATION_METHODS),
  notes: z.string().trim().max(2000).nullish(),
};

export const purchaseInputSchema = z.object({
  ...purchaseFields,
  description: purchaseFields.description.default(''),
  defaultCondition: purchaseFields.defaultCondition.default('U'),
  currency: purchaseFields.currency.default('GBP'),
  vatScheme: purchaseFields.vatScheme.default('none'),
  allocationMethod: purchaseFields.allocationMethod.default('by_value'),
});

export const purchasePatchSchema = z.object(purchaseFields).partial();

const batchFields = {
  name: z.string().trim().min(1).max(120),
  plannedOnlineAt: z.iso.datetime({ offset: true }).nullish(),
};
export const batchInputSchema = z.object(batchFields);
export const batchPatchSchema = z.object(batchFields).partial();

export const stockRowSchema = z.object({
  itemType: itemTypeSchema,
  itemNo: z.string().trim().min(1).max(40),
  /** Only needed when the item is not in the catalogue yet. */
  itemName: z.string().trim().max(300).optional(),
  categoryId: z.number().int().nullish(),
  colorId: z.number().int().min(0),
  condition: conditionSchema,
  subCondition: subConditionSchema.nullish(),
  qty: z.number().int().min(1).max(1_000_000),
  locationId: z.number().int().nullish(),
  /** Unit cost. Leave out to allocate from the purchase price later. */
  unitCost: moneySchema.nullish(),
  price: moneySchema.nullish(),
  remarks: z.string().trim().max(500).nullish(),
  description: z.string().trim().max(500).nullish(),
  bulk: z.number().int().min(1).max(100000).nullish(),
});
export type StockRow = z.infer<typeof stockRowSchema>;

export const addStockSchema = z.object({
  rows: z.array(stockRowSchema).min(1).max(5000),
});

export const adjustStockSchema = z.object({
  /** Positive adds, negative removes. */
  delta: z.number().int().refine((n) => n !== 0, 'Change cannot be zero'),
  reason: z.enum(['adjust', 'found', 'lost', 'damaged']).default('adjust'),
  note: z.string().trim().max(300).nullish(),
});

export const moveStockSchema = z.object({
  locationId: z.number().int().nullable(),
});

// ---------------------------------------------------------------- imports

/** A parsed import row sent back to be committed. Same shape the parser produces. */
export const importRowSchema = z.object({
  line: z.number().int(),
  itemType: itemTypeSchema,
  itemNo: z.string().trim().min(1).max(40),
  itemName: z.string().max(300).optional(),
  categoryId: z.number().int().nullish(),
  categoryName: z.string().max(200).optional(),
  colorId: z.number().int().min(0).nullable(),
  colorName: z.string().max(100).optional(),
  condition: conditionSchema,
  subCondition: subConditionSchema.nullable(),
  qty: z.number().int().min(1).max(1_000_000),
  price: z.number().min(0).nullable(),
  cost: z.number().min(0).nullable(),
  bulk: z.number().int().min(1).nullable(),
  remarks: z.string().max(500).optional(),
  description: z.string().max(500).optional(),
  locationCode: z.string().max(60).nullish(),
  stockroom: z.boolean().optional(),
});

export const importCommitSchema = z.object({
  rows: z.array(importRowSchema).min(1).max(20000),
  /** Create locations named in the file that do not exist yet. */
  createLocations: z.boolean().default(true),
  /** Set a lot's price from the file when the lot has none. */
  applyPrices: z.boolean().default(true),
  /** Import the rows that resolve and report the rest, instead of refusing the whole file. */
  skipUnresolved: z.boolean().default(false),
});

export const importParseSchema = z.object({
  content: z.string().min(1).max(25_000_000),
  format: z.enum(['auto', 'bsx', 'bricklink-xml', 'csv']).default('auto'),
  options: z
    .object({
      defaultItemType: itemTypeSchema.optional(),
      defaultCondition: conditionSchema.optional(),
      remarksAsLocation: z.boolean().optional(),
    })
    .default({}),
});

// ---------------------------------------------------------------- channels

export const channelKindSchema = z.enum(['bricklink', 'brickowl']);

export const channelConfigSchema = z.object({
  /** off, read only, or read_write (which lets BrickLoop change the store, and needs confirmWrites). */
  mode: z.enum(['off', 'read_only', 'read_write']).optional(),
  /** Must be true to switch writing on: it lets BrickLoop create and change lots in your real store. */
  confirmWrites: z.boolean().optional(),
  settings: z
    .object({
      currency: z.string().length(3).optional(),
      remarksAsLocation: z.boolean().optional(),
      /** Which Brick Owl grade an ordinary Used lot is listed as. Brick Owl has three, BrickLoop has one. */
      brickowlUsedCondition: z.enum(['usedn', 'usedg', 'useda']).optional(),
    })
    .optional(),
  /** BrickLink's four OAuth values, or Brick Owl's single API key. The channel in the address says which is expected. */
  credentials: z
    .union([
      z.object({
        consumerKey: z.string().min(1),
        consumerSecret: z.string().min(1),
        token: z.string().min(1),
        tokenSecret: z.string().min(1),
      }),
      z.object({ apiKey: z.string().min(1) }),
    ])
    .optional(),
});

// ---------------------------------------------------------------- auth and admin

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().max(254),
  password: z.string().min(1).max(200),
});

export const USER_ROLES = ['owner', 'staff', 'picker'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const userInputSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  name: z.string().trim().min(1).max(100),
  role: z.enum(USER_ROLES),
  password: z.string().min(12, 'Use at least 12 characters').max(200),
});

export const INVENTORY_MODES = ['unconsolidated', 'consolidated'] as const;
export const AUTO_PRICE_PLANS = ['market', 'quick', 'patient', 'floor', 'cost_plus', 'breakeven'] as const;
export const settingsSchema = z
  .object({
    inventoryMode: z.enum(INVENTORY_MODES),
    defaultCurrency: z.string().length(3),
    /** Whose sales and listings the sell-through figures count. Worldwide unless changed. */
    sellThroughRegion: z.enum(PRICE_GUIDE_REGIONS),
    /** Whether postage and insurance on a purchase is a running cost (absorb) or added to the parts' cost (spread). */
    postageTreatment: z.enum(['absorb', 'spread']),
    /** Whether lots with no price are priced in line with the market when BrickLink's figures arrive. */
    autoPrice: z.boolean(),
    /** The suggestion used when BrickLoop prices a newly added lot. */
    autoPricePlan: z.enum(AUTO_PRICE_PLANS),
    /** Automatic prices never go below what the stock cost: where the market price would, the lot is priced at cost instead. */
    neverBelowCost: z.boolean(),
  })
  .partial();

// ---------------------------------------------------------------- bins from a CSV

export const locationImportSchema = z.object({
  content: z.string().min(1).max(5_000_000),
  /** Report what would happen without creating anything. */
  dryRun: z.boolean().default(false),
  /** Bins that already exist are left alone unless this is on, which updates them from the columns the file has. */
  updateExisting: z.boolean().default(false),
});

// ---------------------------------------------------------------- items and their colour children

const optionalColorIds = z.array(z.number().int().min(0)).max(1000);

const itemFields = {
  name: z.string().trim().min(1).max(300),
  categoryId: z.number().int().nullish(),
  weightG: z.number().min(0).max(10_000_000).nullish(),
  yearReleased: z.number().int().min(1900).max(2200).nullish(),
};

export const itemInputSchema = z.object({
  itemType: itemTypeSchema,
  /** The BrickLink item number, kept exactly as given. */
  itemNo: z.string().trim().min(1).max(40),
  ...itemFields,
  /** Colour children to create with it. Items that have no colour (sets, minifigures) always get colour 0. */
  colorIds: optionalColorIds.default([]),
  /** What BrickLink said about it, when the form was filled in from BrickLink. */
  dimX: z.number().min(0).max(100000).nullish(),
  dimY: z.number().min(0).max(100000).nullish(),
  dimZ: z.number().min(0).max(100000).nullish(),
  alternateNo: z.string().trim().max(100).nullish(),
  description: z.string().trim().max(2000).nullish(),
  isObsolete: z.boolean().nullish(),
  /** True when the details above came from BrickLink, which makes this a full record rather than a basic one. */
  fromBricklink: z.boolean().optional(),
});

export const itemPatchSchema = z.object(itemFields).partial();

export const addVariantsSchema = z.object({ colorIds: optionalColorIds.min(1) });

export const variantPatchSchema = z
  .object({
    defaultLocationNewId: z.number().int().nullable(),
    defaultLocationUsedId: z.number().int().nullable(),
    notes: z.string().trim().max(500).nullable(),
    active: z.boolean(),
  })
  .partial();

export const itemSearchSchema = z.object({
  q: optionalText,
  itemType: z.preprocess(blank, itemTypeSchema.optional()),
  /** By default only items that have been set up (have colour children) are listed; turn off to search the whole catalogue. */
  setUp: z.preprocess((v) => (v === 'false' || v === '0' ? false : v === 'true' || v === '1' ? true : blank(v)), z.boolean().default(true)),
  /** Only items with pieces on order. */
  incoming: optionalBool,
  page: z.preprocess(blank, z.coerce.number().int().min(1).default(1)),
  pageSize: z.preprocess(blank, z.coerce.number().int().min(1).max(200).default(50)),
});

// ---------------------------------------------------------------- purchase orders and receipts

const orderCharge = moneySchema.default('0.0000');

const orderFields = {
  /** When the order was placed. */
  purchasedOn: z.iso.date(),
  description: z.string().trim().max(200),
  sellerName: z.string().trim().max(120).nullish(),
  sellerContact: z.string().trim().max(500).nullish(),
  currency: z.string().length(3),
  /** Units of the default currency per unit of the order's currency. Needed to receive an order in another currency. */
  exchangeRate: z.number().positive().max(1_000_000).nullish(),
  vatScheme: z.enum(VAT_SCHEMES),
  notes: z.string().trim().max(2000).nullish(),
  shippingCost: moneySchema,
  insuranceCost: moneySchema,
  otherCharges: moneySchema,
  taxAmount: moneySchema,
  discountAmount: moneySchema,
  trackingNo: z.string().trim().max(120).nullish(),
  trackingLink: z.string().trim().max(500).nullish(),
  shippedOn: z.iso.date().nullish(),
};

export const orderLineInputSchema = z.object({
  itemType: itemTypeSchema,
  itemNo: z.string().trim().min(1).max(40),
  /** Only needed when the item is not in the catalogue yet. */
  itemName: z.string().trim().max(300).optional(),
  categoryId: z.number().int().nullish(),
  colorId: z.number().int().min(0),
  condition: conditionSchema,
  subCondition: subConditionSchema.nullish(),
  qty: z.number().int().min(1).max(1_000_000),
  /** Price of one piece in the order's currency. */
  unitPrice: moneySchema,
  note: z.string().trim().max(500).nullish(),
  /** The marketplace's own id for the lot this line came from. */
  externalRef: z.string().trim().max(60).nullish(),
});

export const orderInputSchema = z.object({
  ...orderFields,
  description: orderFields.description.default(''),
  currency: orderFields.currency.default('GBP'),
  vatScheme: orderFields.vatScheme.default('none'),
  shippingCost: orderCharge,
  insuranceCost: orderCharge,
  otherCharges: orderCharge,
  taxAmount: orderCharge,
  discountAmount: orderCharge,
  /** Where it is now. Receiving goods moves it on by itself. */
  status: z.enum(['ordered', 'in_transit']).default('ordered'),
  lines: z.array(orderLineInputSchema).min(1).max(2000),
});

/** Header fields only. Postage, charges, tax and currency are locked once any goods have been receipted. */
export const orderPatchSchema = z.object(orderFields).partial();

export const orderLinePatchSchema = z
  .object({
    qtyOrdered: z.number().int().min(1).max(1_000_000),
    unitPrice: moneySchema,
  })
  .partial();

export const orderActionSchema = z.object({ action: z.enum(ORDER_ACTIONS) });

export const receiptInputSchema = z.object({
  receivedOn: z.iso.date(),
  inboundShipmentId: z.number().int().optional(),
  /** A delivery note number, or anything that helps find the parcel again. */
  reference: z.string().trim().max(120).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /** The person counting has confirmed that any pieces above the order are free extras. */
  confirmFreeOverages: z.boolean().optional(),
  lines: z
    .array(
      z.object({
        purchaseLineId: z.number().int(),
        qty: z.number().int().min(1).max(1_000_000),
        /** Leave out to use the colour child's default location. */
        locationId: z.number().int().nullish(),
      }),
    )
    .min(1)
    .max(2000),
});

/** The parcel is here, but not counted yet: nothing goes into stock until each purchase is received with a count. */
export const inboundShipmentArrivalSchema = z.object({
  arrivedOn: z.iso.date(),
  notes: z.string().trim().max(500).nullish(),
});

export const purchaseListSchema = z.object({
  /** coming_in is everything still expected: ordered, in transit or partly received. */
  status: z.preprocess(blank, z.enum(['all', 'coming_in', ...ORDER_STATUSES]).default('all')),
  source: z.preprocess(blank, z.enum(['purchases', 'partout']).default('purchases')),
});

/** Pulling the orders you placed from BrickLink. `from` is the earliest order date to bring in. */
export const bricklinkOrdersImportSchema = z.object({ from: z.iso.date().optional() });

// ---------------------------------------------------------------- suppliers

const supplierFields = {
  name: z.string().trim().min(1).max(120),
  contact: z.string().trim().max(500).nullable(),
  notes: z.string().trim().max(4000).nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20),
  active: z.boolean(),
};
export const supplierInputSchema = z.object({ name: supplierFields.name, contact: supplierFields.contact.optional(), notes: supplierFields.notes.optional(), tags: supplierFields.tags.optional() });
export const supplierPatchSchema = z.object(supplierFields).partial();
export const supplierListSchema = z.object({
  q: optionalText,
  sort: z.preprocess(blank, z.enum(['name', 'spent', 'orders', 'recent']).default('name')),
  includeInactive: optionalBool,
});
export const supplierMergeSchema = z.object({ intoId: z.number().int() });

// ---------------------------------------------------------------- customers

const customerFields = {
  name: z.string().trim().min(1).max(120),
  email: z.email().max(320).nullable(),
  contact: z.string().trim().max(500).nullable(),
  notes: z.string().trim().max(4000).nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20),
  active: z.boolean(),
};
export const customerInputSchema = z.object({ name: customerFields.name, email: customerFields.email.optional(), contact: customerFields.contact.optional(), notes: customerFields.notes.optional(), tags: customerFields.tags.optional() });
export const customerPatchSchema = z.object(customerFields).partial();
export const customerListSchema = z.object({
  q: optionalText,
  sort: z.preprocess(blank, z.enum(['name', 'spent', 'orders', 'recent']).default('name')),
  includeInactive: optionalBool,
});
export const customerMergeSchema = z.object({ intoId: z.number().int() });
export const customerAddressSchema = z.object({
  label: z.string().trim().min(1).max(80), attention: z.string().trim().max(120).nullable(), addressee: z.string().trim().max(120).nullable(), phone: z.string().trim().max(40).nullable(),
  line1: z.string().trim().min(1).max(120), line2: z.string().trim().max(120).nullable(), city: z.string().trim().min(1).max(80), county: z.string().trim().max(80).nullable(), postcode: z.string().trim().min(1).max(24), country: z.string().trim().min(1).max(80), residential: z.boolean(), defaultBilling: z.boolean(), defaultShipping: z.boolean(),
});

// ---------------------------------------------------------------- parting out sets

export const partOutPreviewSchema = z.object({
  /** How many of the sets on hand to break up. */
  qty: z.number().int().min(1).max(10000),
  extras: z.boolean().default(false),
  breakMinifigs: z.boolean().default(false),
  method: z.enum(['by_value', 'equal']).default('by_value'),
});
export const partOutSchema = partOutPreviewSchema.extend({
  /** The date the parts count as coming in, so it can be backdated. */
  partedOn: z.iso.date(),
  /** What was actually counted of each part, which can differ from what was expected. Leave out to count nothing yet. */
  counted: z
    .array(z.object({ itemType: itemTypeSchema, itemNo: z.string().trim().min(1).max(40), colorId: z.number().int().min(0), qty: z.number().int().min(0).max(1_000_000) }))
    .max(2000)
    .optional(),
});

export const bricklinkSyncPlanSchema = z.object({
  /** Read BrickLink's inventory again first, so the plan is against what is there now. */
  refresh: z.boolean().default(true),
  /** Also set lots that are sold out here, but still listed on BrickLink, to quantity 0. */
  includeZero: z.boolean().default(false),
});
export const bricklinkSyncApplySchema = z.object({
  /** Send only these lots. Leave out to send everything in the plan, up to the most one run may send. */
  lotIds: z.array(z.number().int()).min(1).max(100).optional(),
  includeZero: z.boolean().default(false),
  /** Take the chosen lots down from BrickLink (set to zero and keep them). Needs lotIds. Only works on lots that are not for sale here. */
  takeDown: z.boolean().default(false),
});

export const bricklinkSalesImportSchema = z.object({
  /** The earliest sale to bring in. */
  from: z.iso.date().optional(),
  /** Whether sold pieces are taken out of stock. */
  adjustStock: z.boolean().optional(),
});

// ---------------------------------------------------------------- opening stock: swapping it for different stock

export const openStockSchema = z.object({
  /** How many of this lot to take out. */
  qty: z.number().int().min(1).max(10000),
  openedOn: z.iso.date(),
  method: z.enum(['by_value', 'equal']).default('by_value'),
  note: z.string().trim().max(500).nullish(),
  outputs: z
    .array(
      z.object({
        itemType: itemTypeSchema,
        itemNo: z.string().trim().min(1).max(40),
        colorId: z.number().int().min(0),
        condition: conditionSchema.optional(),
        qty: z.number().int().min(1).max(100000),
        name: z.string().trim().max(250).optional(),
      }),
    )
    .min(1)
    .max(50),
});

// ---------------------------------------------------------------- parts lists and building

export const bomSaveSchema = z.object({
  lines: z
    .array(
      z.object({
        itemType: itemTypeSchema,
        itemNo: z.string().trim().min(1).max(40),
        colorId: z.number().int().min(0),
        /** Null means the same condition as the assembly being built. */
        condition: conditionSchema.nullable().default(null),
        qty: z.number().int().min(1).max(1000),
        name: z.string().trim().max(250).nullish(),
        categoryId: z.number().int().nullish(),
      }),
    )
    .max(200),
  source: z.enum(['manual', 'bricklink']).default('manual'),
});
export const assemblyLotSchema = z.object({ condition: conditionSchema });
export const buildSchema = z.object({
  qty: z.number().int().min(1).max(100000),
  on: z.iso.date(),
  note: z.string().trim().max(500).nullish(),
});

// ---------------------------------------------------------------- stock status and transfers

export const stockStatusSchema = z.object({
  status: z.enum(['good', 'damaged', 'quarantine']),
  /** How many of the entry's pieces. Leave out for all of them. */
  qty: z.number().int().min(1).max(1_000_000).optional(),
  note: z.string().trim().max(300).nullish(),
});
export const transferSchema = z.object({
  fromLocationId: z.number().int(),
  toLocationId: z.number().int(),
  notes: z.string().trim().max(500).nullish(),
  lines: z.array(z.object({ lotId: z.number().int(), qty: z.number().int().min(1).max(1_000_000) })).min(1).max(200),
});
export const transferShipSchema = z.object({ on: z.iso.date() });
export const transferReceiveSchema = z.object({
  on: z.iso.date(),
  lines: z.array(z.object({ lineId: z.number().int(), qtyReceived: z.number().int().min(0).max(1_000_000) })).optional(),
});

// ---------------------------------------------------------------- costing

export const entryCostSchema = z.object({ unitCost: moneySchema, reason: z.string().trim().max(300).nullish() });
export const lotCostSchema = z.object({ unitCost: moneySchema, onlyUnknown: z.boolean().default(true), reason: z.string().trim().max(300).nullish() });

// ---------------------------------------------------------------- price levels

export const priceLevelSchema = z.object({
  name: z.string().trim().min(1).max(60),
  /** Added to the base price: -10 is ten percent under. */
  percent: z.string().regex(/^-?\d{1,3}(\.\d{1,2})?$/, 'Enter a percentage such as -10 or 15').refine((v) => Number(v) > -100 && Number(v) <= 500, 'Between -99.99 and 500'),
  active: z.boolean().optional(),
});

// ---------------------------------------------------------------- bills, payments and buying

export const billSchema = z.object({
  purchaseId: z.number().int().nullish(),
  supplierId: z.number().int().nullish(),
  billDate: z.iso.date(),
  dueOn: z.iso.date().nullish(),
  reference: z.string().trim().max(120).nullish(),
  amount: moneySchema.nullish(),
  currency: z.string().trim().length(3).nullish(),
  notes: z.string().trim().max(500).nullish(),
  paid: z.object({ paidOn: z.iso.date(), method: z.string().trim().max(80).nullish(), reference: z.string().trim().max(120).nullish() }).nullish(),
});
export const paymentSchema = z.object({
  supplierId: z.number().int().nullish(),
  paidOn: z.iso.date(),
  method: z.string().trim().max(80).nullish(),
  reference: z.string().trim().max(120).nullish(),
  currency: z.string().trim().length(3),
  notes: z.string().trim().max(500).nullish(),
  applications: z.array(z.object({ billId: z.number().int(), amount: moneySchema })).min(1).max(50),
});
export const itemVendorSchema = z.object({
  vendorCode: z.string().trim().max(80).nullish(),
  preferred: z.boolean().default(false),
  leadTimeDays: z.number().int().min(0).max(1000).nullish(),
  notes: z.string().trim().max(500).nullish(),
});
export const reorderRuleSchema = z.object({
  itemId: z.number().int(),
  colorId: z.number().int().min(0),
  condition: conditionSchema,
  minQty: z.number().int().min(0).max(1_000_000),
  targetQty: z.number().int().min(0).max(1_000_000),
  supplierId: z.number().int().nullish(),
});
export const buyListOrderSchema = z.object({
  supplierId: z.number().int(),
  ruleIds: z.array(z.number().int()).min(1).max(500),
  orderedOn: z.iso.date(),
  description: z.string().trim().max(200).nullish(),
});

export const itemTypeChangeSchema = z.object({ itemType: itemTypeSchema });

// ---------------------------------------------------------------- putting stock away

export const putAwayQuerySchema = z.object({ purchaseId: optionalInt });
export const putAwaySchema = z.object({
  moves: z.array(z.object({ entryId: z.number().int(), locationId: z.number().int() })).min(1).max(2000),
});

// ---------------------------------------------------------------- stocktaking

export const stocktakeRowSaveSchema = z.object({
  /** Present to update an existing count row; absent to add a newly found item. */
  id: optionalInt,
  itemType: itemTypeSchema,
  itemNo: z.string().trim().min(1).max(40),
  colorId: z.number().int().min(0),
  condition: conditionSchema,
  subCondition: subConditionSchema.nullish(),
  /** Null means "not counted"; an explicit zero means "checked and none found". */
  countedQty: z.number().int().min(0).max(1_000_000).nullable(),
  note: z.string().trim().max(500).nullish(),
});

export const saveStocktakeSchema = z.object({
  /** Rejects a save when another window has already saved (optimistic concurrency). */
  draftRevision: z.number().int().min(0),
  note: z.string().trim().max(2000).nullish(),
  rows: z.array(stocktakeRowSaveSchema).max(5000),
});

export const applyStocktakeSchema = z.object({
  scope: z.enum(['full', 'partial']),
  draftRevision: z.number().int().min(0),
});
export const inboundShipmentPatchSchema = z.object({ reference: z.string().trim().min(1).max(120).optional(), /** 'received' is not settable by hand: it only follows from counting every purchase in. */ status: z.enum(['planned','dispatched','delayed','arrived','cancelled']).optional(), expectedShipOn: z.iso.date().nullable().optional(), actualShipOn: z.iso.date().nullable().optional(), expectedReceiptOn: z.iso.date().optional(), actualReceiptOn: z.iso.date().nullable().optional(), carrier: z.string().trim().max(80).nullable().optional(), trackingNo: z.string().trim().max(120).nullable().optional(), notes: z.string().trim().max(1000).nullable().optional() });
export const inboundShipmentSchema = z.object({ reference: z.string().trim().min(1).max(120), expectedShipOn: z.iso.date().nullable().optional(), expectedReceiptOn: z.iso.date(), carrier: z.string().trim().max(80).nullable(), trackingNo: z.string().trim().max(120).nullable(), notes: z.string().trim().max(1000).nullable(), purchaseIds: z.array(z.number().int()).min(1).max(100) });

/** An off-platform sale entered in BrickLoop. The lot is selected directly so stock always comes from the exact item, colour and condition sold. */
export const manualSaleSchema = z.object({
  reference: z.string().trim().min(1).max(80),
  orderedOn: z.iso.date(),
  customerId: z.number().int(),
  currency: z.string().trim().length(3).default('GBP'),
  shipping: moneySchema.default('0'),
  paymentStatus: z.enum(['UNPAID', 'PAID']).default('UNPAID'),
  paymentMethod: z.string().trim().max(80).nullable().optional(),
  shippingMethod: z.string().trim().max(80).nullable().optional(),
  shippingAddress: z.string().trim().max(1_000).nullable().optional(),
  lines: z.array(z.object({
    lotId: z.number().int(),
    qty: z.number().int().min(1).max(1_000_000),
    unitPrice: moneySchema,
  })).min(1).max(100),
});
export type ManualSaleInput = z.infer<typeof manualSaleSchema>;

export const manualSaleStateSchema = z.object({
  state: z.enum(['open', 'shipped', 'completed', 'cancelled']),
});

/** Local picking and dispatch only. BrickLink is never updated by this workflow. */
export const saleFulfilmentSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('pick') }),
  z.object({ action: z.literal('pack'), parcelWeightGrams: z.number().int().min(1).max(100_000).nullable().optional() }),
  z.object({
    action: z.literal('dispatch'),
    carrier: z.string().trim().min(1).max(80),
    trackingNo: z.string().trim().max(120).nullable().optional(),
    trackingLink: z.string().trim().max(500).nullable().optional(),
    notes: z.string().trim().max(1_000).nullable().optional(),
  }),
]);
export type SaleFulfilmentInput = z.infer<typeof saleFulfilmentSchema>;
